<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MarketingCampaignsAPI from 'dashboard/api/marketingCampaigns';
import TrackingSourcesAPI from 'dashboard/api/trackingSources';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import LinkBuilder from 'dashboard/components-next/Attribution/LinkBuilder.vue';
import AttributionCodesTable from 'dashboard/components-next/Attribution/AttributionCodesTable.vue';
import AttributionPerformance from 'dashboard/components-next/Attribution/AttributionPerformance.vue';
import AttributionCodeDialog from 'dashboard/components-next/Attribution/AttributionCodeDialog.vue';

// "Atribución" screen of the CrmB mockup: the UTM codes of the account with
// their link builder, and how those codes performed. Both tabs read from the
// marketing campaigns API that already existed under Ajustes.
const { t, n } = useI18n();

const RANGES = [30, 7, 90];

const campaigns = ref([]);
const sources = ref([]);
const reports = ref({
  total_leads: 0,
  sources: {},
  campaigns: {},
  mediums: {},
  funnel: {},
});
const isFetching = ref(false);
const isFetchingReports = ref(false);
const activeTab = ref('codes');
const searchQuery = ref('');
const rangeDays = ref(RANGES[0]);

const codeDialogRef = ref(null);

const tabs = computed(() => [
  { key: 'codes', label: t('ATTRIBUTION.TABS.CODES') },
  { key: 'performance', label: t('ATTRIBUTION.TABS.PERFORMANCE') },
]);

const rangeLabel = computed(() =>
  t('ATTRIBUTION.FILTERS.LAST_DAYS', { count: rangeDays.value })
);

// The landing page the builder starts from: the most used one across the
// codes, falling back to the account's own host.
const landingUrl = computed(() => {
  const stored = campaigns.value.find(campaign => campaign.utms?.landing)?.utms
    ?.landing;
  return stored || window.location.origin;
});

const filteredCampaigns = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return campaigns.value;
  return campaigns.value.filter(campaign =>
    [
      campaign.code,
      campaign.description,
      campaign.utms?.source,
      campaign.utms?.campaign,
      campaign.utms?.content,
    ]
      .filter(Boolean)
      .some(field => String(field).toLowerCase().includes(query))
  );
});

const attributedLeads = computed(() => Number(reports.value.total_leads) || 0);

const headerSummary = computed(() =>
  t('ATTRIBUTION.HEADER.SUMMARY', {
    codes: n(campaigns.value.length),
    leads: n(attributedLeads.value),
  })
);

const fetchCampaigns = async () => {
  isFetching.value = true;
  try {
    const { data } = await MarketingCampaignsAPI.get();
    campaigns.value = data.payload || [];
  } catch {
    useAlert(t('ATTRIBUTION.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const fetchSources = async () => {
  try {
    const { data } = await TrackingSourcesAPI.get();
    sources.value = data.payload || data || [];
  } catch {
    sources.value = [];
  }
};

const fetchReports = async () => {
  isFetchingReports.value = true;
  const start = new Date();
  start.setDate(start.getDate() - rangeDays.value);
  try {
    const { data } = await MarketingCampaignsAPI.getReports({
      start_date: start.toISOString().slice(0, 10),
      end_date: new Date().toISOString().slice(0, 10),
    });
    reports.value = { funnel: {}, ...data };
  } catch {
    useAlert(t('ATTRIBUTION.FETCH_ERROR'));
  } finally {
    isFetchingReports.value = false;
  }
};

// Cycles 30 → 7 → 90 días, the ranges the report filter offers.
const cycleRange = () => {
  const index = RANGES.indexOf(rangeDays.value);
  rangeDays.value = RANGES[(index + 1) % RANGES.length];
};

watch(rangeDays, fetchReports);

const openCreate = () => codeDialogRef.value?.open();
const openEdit = campaign => codeDialogRef.value?.open(campaign);

const refresh = () => {
  fetchCampaigns();
  fetchReports();
};

// CSV of the funnel table, so the numbers can go into a spreadsheet.
const exportCsv = () => {
  const funnel = reports.value.funnel || {};
  const rows = Object.entries(funnel).map(([code, row]) =>
    [code, row.leads || 0, row.quoted || 0, row.won || 0].join(',')
  );
  if (!rows.length) {
    useAlert(t('ATTRIBUTION.EMPTY_RANGE'));
    return;
  }
  const csv = ['codigo,leads,cotizados,ganados', ...rows].join('\n');
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  link.download = `atribucion-${rangeDays.value}d.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

onMounted(() => {
  fetchCampaigns();
  fetchSources();
  fetchReports();
});
</script>

<template>
  <section
    class="flex flex-col w-full h-full min-h-0 overflow-hidden bg-n-background"
  >
    <header
      class="flex flex-col px-6 pt-[18px] bg-n-solid-2 border-b border-n-weak"
    >
      <div class="flex flex-wrap items-center gap-3">
        <h1
          class="m-0 text-[19px] font-semibold tracking-tight text-n-slate-12"
        >
          {{ t('ATTRIBUTION.HEADER.TITLE') }}
        </h1>
        <span class="text-[12.5px] font-medium text-n-slate-10">
          {{ headerSummary }}
        </span>
        <div class="flex items-center gap-2 ml-auto">
          <label
            class="relative flex items-center w-[250px] max-w-[40vw] h-[34px] rounded-lg border border-n-weak text-n-slate-10 focus-within:border-n-brand/60 transition-colors"
          >
            <Icon
              icon="i-lucide-search"
              class="absolute ltr:left-3 rtl:right-3 size-[15px]"
            />
            <input
              v-model="searchQuery"
              type="search"
              :placeholder="t('ATTRIBUTION.HEADER.SEARCH_PLACEHOLDER')"
              class="w-full h-full bg-transparent border-0 outline-none reset-base text-[13px] text-n-slate-12 placeholder:text-n-slate-10 ltr:pl-9 rtl:pr-9 ltr:pr-3 rtl:pl-3"
            />
          </label>
          <Button
            icon="i-lucide-plus"
            size="sm"
            class="h-[34px] rounded-lg"
            :label="t('ATTRIBUTION.HEADER.NEW_CODE')"
            @click="openCreate"
          />
        </div>
      </div>
      <div class="flex items-center gap-6 pt-3.5">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="pb-2.5 -mb-px text-[13px] border-b-2 transition-colors"
          :class="
            activeTab === tab.key
              ? 'font-semibold text-n-slate-12 border-n-brand'
              : 'font-medium text-n-slate-10 border-transparent hover:text-n-slate-12'
          "
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </div>
    </header>

    <main class="flex-1 min-h-0 overflow-y-auto px-6 pt-[18px] pb-8">
      <div
        v-if="isFetching"
        class="flex items-center justify-center py-16 text-n-slate-11"
      >
        <Spinner />
      </div>

      <div
        v-else-if="activeTab === 'codes'"
        class="flex flex-col gap-4 max-w-[1120px]"
      >
        <LinkBuilder :campaigns="campaigns" :default-url="landingUrl" />
        <AttributionCodesTable
          v-if="filteredCampaigns.length"
          :campaigns="filteredCampaigns"
          :landing-url="landingUrl"
          @edit="openEdit"
        />
        <div
          v-else
          class="flex flex-col items-center gap-2 py-16 text-center rounded-xl border border-n-weak bg-n-solid-2"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{
              searchQuery
                ? t('ATTRIBUTION.EMPTY_SEARCH')
                : t('ATTRIBUTION.EMPTY_TITLE')
            }}
          </span>
          <span class="max-w-[320px] text-xs leading-relaxed text-n-slate-10">
            {{ t('ATTRIBUTION.EMPTY_DESCRIPTION') }}
          </span>
          <Button
            v-if="!searchQuery"
            icon="i-lucide-plus"
            size="sm"
            class="mt-2"
            :label="t('ATTRIBUTION.HEADER.NEW_CODE')"
            @click="openCreate"
          />
        </div>
      </div>

      <AttributionPerformance
        v-else
        class="max-w-[1120px]"
        :reports="reports"
        :campaigns="campaigns"
        :is-loading="isFetchingReports"
        :range-label="rangeLabel"
        @change-range="cycleRange"
        @export="exportCsv"
      />
    </main>

    <AttributionCodeDialog
      ref="codeDialogRef"
      :sources="sources"
      @saved="refresh"
      @deleted="refresh"
    />
  </section>
</template>
